import type {
  BillingPreference,
  CreateCustomerContactInput,
  CreateCustomerInput,
  CustomerListQuery,
  CustomerRestrictionInput,
  CustomerRestrictionView,
  Page,
  UpdateCustomerContactInput,
  UpdateCustomerInput,
} from '@chukua/common';
import { api } from '../../shared/shared.api';
import { toListParams } from '../../shared/policies/list-params';

export interface CustomerListItem {
  id: string;
  name: string;
  kraPin: string;
  billingPreference: BillingPreference;
  isActive: boolean;
  createdAt: string;
}

export interface CustomerContact {
  id: string;
  name: string;
  role: string | null;
  phone: string | null;
  email: string | null;
}

export interface CustomerView {
  id: string;
  name: string;
  kraPin: string;
  billingAddress: { line1: string; line2?: string; city: string; country: string };
  paymentTermsDays: number;
  creditLimit: { amountMinor: number; currency: string };
  billingPreference: BillingPreference;
  poNumberRequired: boolean;
  isContracted: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerDetail extends CustomerView {
  contacts: CustomerContact[];
}

export function listCustomers(query: CustomerListQuery): Promise<Page<CustomerListItem>> {
  return api<Page<CustomerListItem>>(`/customers?${toListParams(query)}`);
}

export async function createCustomer(input: CreateCustomerInput): Promise<void> {
  await api<void>('/customers', { method: 'POST', body: JSON.stringify(input) });
}

export function getCustomer(id: string): Promise<CustomerDetail> {
  return api<CustomerDetail>(`/customers/${id}`);
}

export async function updateCustomer(id: string, input: UpdateCustomerInput): Promise<void> {
  await api<void>(`/customers/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
}

export async function activateCustomer(id: string): Promise<void> {
  await api<void>(`/customers/${id}/activate`, { method: 'POST' });
}

export async function deactivateCustomer(id: string): Promise<void> {
  await api<void>(`/customers/${id}/deactivate`, { method: 'POST' });
}

export async function addCustomerContact(id: string, input: CreateCustomerContactInput): Promise<void> {
  await api<void>(`/customers/${id}/contacts`, { method: 'POST', body: JSON.stringify(input) });
}

export async function updateCustomerContact(id: string, contactId: string, input: UpdateCustomerContactInput): Promise<void> {
  await api<void>(`/customers/${id}/contacts/${contactId}`, { method: 'PATCH', body: JSON.stringify(input) });
}

export async function removeCustomerContact(id: string, contactId: string): Promise<void> {
  await api<void>(`/customers/${id}/contacts/${contactId}`, { method: 'DELETE' });
}

export async function listCustomerRestrictions(id: string): Promise<CustomerRestrictionView[]> {
  return api<CustomerRestrictionView[]>(`/customers/${id}/restrictions`);
}

export async function addCustomerRestriction(id: string, input: CustomerRestrictionInput): Promise<void> {
  await api<void>(`/customers/${id}/restrictions`, { method: 'POST', body: JSON.stringify(input) });
}

export async function updateCustomerRestriction(id: string, restrictionId: string, input: CustomerRestrictionInput): Promise<void> {
  await api<void>(`/customers/${id}/restrictions/${restrictionId}`, { method: 'PATCH', body: JSON.stringify(input) });
}

export async function removeCustomerRestriction(id: string, restrictionId: string): Promise<void> {
  await api<void>(`/customers/${id}/restrictions/${restrictionId}`, { method: 'DELETE' });
}
